import fs from 'fs/promises'
import path from 'path'
import os from 'os'
import { IntegrationDefinition } from './types.js'

interface VersionResolution {
  integrationId: string
  updatedAt: string
  cachedAt: string
}

interface CachedDefinition {
  definition: IntegrationDefinition
  cachedAt: string
}

export class EnhancedIntegrationCache {
  private cacheDir: string
  private resolutionsDir: string
  private definitionsDir: string
  private noCache: boolean

  constructor(noCache: boolean = false) {
    this.noCache = noCache
    // Global cache in ~/.adk/cache
    this.cacheDir = path.join(os.homedir(), '.adk', 'cache', 'integrations')
    this.resolutionsDir = path.join(this.cacheDir, 'resolutions')
    this.definitionsDir = path.join(this.cacheDir, 'definitions')
  }

  private async ensureCacheDirs(): Promise<void> {
    await fs.mkdir(this.resolutionsDir, { recursive: true })
    await fs.mkdir(this.definitionsDir, { recursive: true })
  }

  /**
   * Get version resolution from cache (name@version → intver + updatedAt)
   * Returns null if cache miss or expired (5 minutes)
   */
  async getResolution(name: string, version: string, workspace?: string): Promise<VersionResolution | null> {
    if (this.noCache) {
      return null
    }

    try {
      const key = this.getResolutionKey(name, version, workspace)
      const cachePath = path.join(this.resolutionsDir, `${key}.json`)

      const data = await fs.readFile(cachePath, 'utf-8')
      const resolution: VersionResolution = JSON.parse(data)

      // Check if cache is expired (5 minutes)
      const cachedAt = new Date(resolution.cachedAt)
      const now = new Date()
      const ageMinutes = (now.getTime() - cachedAt.getTime()) / (1000 * 60)

      if (ageMinutes > 5) {
        // Cache expired
        return null
      }

      return resolution
    } catch {
      // Cache miss or error
      return null
    }
  }

  /**
   * Cache version resolution (name@version → intver + updatedAt)
   */
  async setResolution(
    name: string,
    version: string,
    workspace: string | undefined,
    integrationId: string,
    updatedAt: string
  ): Promise<void> {
    await this.ensureCacheDirs()

    const key = this.getResolutionKey(name, version, workspace)
    const cachePath = path.join(this.resolutionsDir, `${key}.json`)

    const resolution: VersionResolution = {
      integrationId,
      updatedAt,
      cachedAt: new Date().toISOString(),
    }

    await fs.writeFile(cachePath, JSON.stringify(resolution, null, 2))
  }

  /**
   * Get integration definition from cache (intver + updatedAt → definition)
   * Never expires for same intver + updatedAt (immutable)
   */
  async getDefinition(integrationId: string, updatedAt: string): Promise<IntegrationDefinition | null> {
    if (this.noCache) {
      return null
    }

    try {
      const key = this.getDefinitionKey(integrationId, updatedAt)
      const cachePath = path.join(this.definitionsDir, `${key}.json`)

      const data = await fs.readFile(cachePath, 'utf-8')
      const cached: CachedDefinition = JSON.parse(data)

      return cached.definition
    } catch {
      // Cache miss or error
      return null
    }
  }

  /**
   * Cache integration definition (intver + updatedAt → definition)
   */
  async setDefinition(integrationId: string, updatedAt: string, definition: IntegrationDefinition): Promise<void> {
    await this.ensureCacheDirs()

    const key = this.getDefinitionKey(integrationId, updatedAt)
    const cachePath = path.join(this.definitionsDir, `${key}.json`)

    const cached: CachedDefinition = {
      definition,
      cachedAt: new Date().toISOString(),
    }

    await fs.writeFile(cachePath, JSON.stringify(cached, null, 2))
  }

  /**
   * Clear all caches
   */
  async clear(): Promise<void> {
    try {
      // Clear resolutions
      const resolutionFiles = await fs.readdir(this.resolutionsDir)
      await Promise.all(resolutionFiles.map((file) => fs.unlink(path.join(this.resolutionsDir, file))))

      // Clear definitions
      const definitionFiles = await fs.readdir(this.definitionsDir)
      await Promise.all(definitionFiles.map((file) => fs.unlink(path.join(this.definitionsDir, file))))
    } catch {
      // Ignore errors if directories don't exist
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    resolutions: { count: number; sizeBytes: number }
    definitions: { count: number; sizeBytes: number }
  }> {
    const getDirectoryStats = async (dir: string) => {
      try {
        const files = await fs.readdir(dir)
        let totalSize = 0

        for (const file of files) {
          const stats = await fs.stat(path.join(dir, file))
          totalSize += stats.size
        }

        return {
          count: files.length,
          sizeBytes: totalSize,
        }
      } catch {
        return { count: 0, sizeBytes: 0 }
      }
    }

    const [resolutions, definitions] = await Promise.all([
      getDirectoryStats(this.resolutionsDir),
      getDirectoryStats(this.definitionsDir),
    ])

    return { resolutions, definitions }
  }

  private getResolutionKey(name: string, version: string, workspace?: string): string {
    // Create a safe filesystem key from name@version
    const prefix = workspace ? `${workspace}_` : ''
    const key = `${prefix}${name}_${version}`
    return key.replace(/[^a-zA-Z0-9_-]/g, '_')
  }

  private getDefinitionKey(integrationId: string, updatedAt: string): string {
    // Create a safe filesystem key from intver_xxx + updatedAt
    const key = `${integrationId}_${updatedAt}`
    return key.replace(/[^a-zA-Z0-9_-]/g, '_')
  }
}
